// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { InputParameterDouble } from '../InputParamaterClasses/InputParameterDouble';
import { Color } from '../../../Graphics';
import { PriceType } from '@shared/utils/History/CashItemUtils';
export class ZZ extends IndicatorScriptBase {
    constructor() {
        super();
        // Initialization
        this.ProjectName = 'ZigZag';
        // Define lines
        this.SetIndicatorLine('UpTrendLine', Color.Yellow, 2, LineStyle.SimpleChart);
        this.SetIndicatorLine('DownTrendLine', Color.Yellow, 2, LineStyle.SimpleChart);
        this.SeparateWindow = false;
        // Input parameters
        this.deviation = 1;
        super.InputParameter(new InputParameterDouble('deviation', 'Percent Deviation', 0, 0, 99999, 2, 0.01));
    }
    GetIndicatorShortName() {
        return `ZigZag(${this.deviation})`;
    }
    Init() {
        this.IndicatorShortName(this.GetIndicatorShortName());
        this.direction = 1;
        this.trendLineLength = 0;
        this.lastPivotPrice = 0;
        this.extremePrice = 0;
        this.extremeBarsAgo = 0;
    }
    NextBar() {
        this.trendLineLength++;
        this.extremeBarsAgo++;
        super.NextBar();
    }
    OnQuote() {
        if (this.CurrentData.Count === 0)
            return;
        const high = this.High(0);
        const low = this.Low(0);
        // First bar initialization
        if (this.CurrentData.Count === 1) {
            this.lastPivotPrice = high;
            this.direction = -1; // Start looking for lows (down leg)
            this.trendLineLength = 1;
            this.extremePrice = high;
            this.extremeBarsAgo = 0;
            this.SetValue(ZZ.UpTrendLineIndex, 0, this.lastPivotPrice);
            this.SetValue(ZZ.DownTrendLineIndex, 0, this.lastPivotPrice);
            return;
        }
        if (this.direction === 1) {
            if (low < this.extremePrice) {
                this.extremePrice = low;
                this.extremeBarsAgo = 0;
            }
            // Draw confirmed segment from pivot to extremum, plus provisional from extremum to current
            this.DrawSegment(ZZ.DownTrendLineIndex, this.trendLineLength - 1, this.extremeBarsAgo, this.lastPivotPrice, this.extremePrice);
            // Confirm reversal when price rises by Deviation from the lowest low
            if (this.extremeBarsAgo > 0 && high >= this.extremePrice * (1.0 + this.deviation / 100.0)) {
                this.direction = -1;
                this.lastPivotPrice = this.extremePrice;
                this.trendLineLength = this.extremeBarsAgo + 1;
                this.extremePrice = high;
                this.extremeBarsAgo = 0;
                this.DrawSegment(ZZ.UpTrendLineIndex, this.trendLineLength - 1, 0, this.lastPivotPrice, high);
                return;
            }
        }
        else {
            // direction === -1
            // Up leg: track TRUE highest high since pivot
            if (high > this.extremePrice) {
                this.extremePrice = high;
                this.extremeBarsAgo = 0;
            }
            // Draw confirmed segment from pivot to extremum, plus provisional from extremum to current
            this.DrawSegment(ZZ.UpTrendLineIndex, this.trendLineLength - 1, this.extremeBarsAgo, this.lastPivotPrice, this.extremePrice);
            // Confirm reversal when price falls by Deviation from the highest high
            if (this.extremeBarsAgo > 0 && low <= this.extremePrice * (1.0 - this.deviation / 100.0)) {
                this.direction = 1;
                this.lastPivotPrice = this.extremePrice;
                this.trendLineLength = this.extremeBarsAgo + 1;
                this.extremePrice = low;
                this.extremeBarsAgo = 0;
                this.DrawSegment(ZZ.DownTrendLineIndex, this.trendLineLength - 1, 0, this.lastPivotPrice, low);
                return;
            }
        }
    }
    DrawSegment(lineIndex, startBarsAgo, endBarsAgo, startPrice, endPrice) {
        const span = startBarsAgo - endBarsAgo;
        if (span < 0)
            return;
        if (span === 0) {
            this.SetValue(lineIndex, endBarsAgo, endPrice);
            return;
        }
        for (let barsAgo = endBarsAgo; barsAgo <= startBarsAgo; barsAgo++) {
            // i represents the position in the interpolation (0 at end, span at start)
            const i = barsAgo - endBarsAgo;
            // Using the formula: y = endPrice - i * (endPrice - startPrice) / span
            // This correctly interpolates between startPrice (at startBarsAgo) and endPrice (at endBarsAgo)
            const y = endPrice - (i * (endPrice - startPrice)) / span;
            this.SetValue(lineIndex, barsAgo, y);
        }
    }
    High(i) {
        return this.CurrentData.getPriceByType(PriceType.High, i);
    }
    Low(i) {
        return this.CurrentData.getPriceByType(PriceType.Low, i);
    }
}
ZZ.UpTrendLineIndex = 0;
ZZ.DownTrendLineIndex = 1;
